import { Link } from "react-router-dom";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { formatCanonicalDateForDisplay } from "@/utils/date";

export default function SEOArticles() {
  const { articles } = useArticleCatalog();
  const featured = articles.find((article) => article.featured) ?? articles[0];
  const others = articles.filter((article) => article.id !== featured?.id).slice(0, 4);

  if (!featured) {
    return null;
  }

  return (
    <section className="py-14 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900">Guias de Compra & Reviews</h2>
            <p className="text-gray-500 text-sm mt-1">Análises detalhadas para você decidir melhor</p>
          </div>
          <Link
            to="/guias"
            className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm transition-colors"
          >
            Ver todos os artigos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Link
            to={`/artigo/${featured.slug}`}
            className="lg:col-span-3 group relative overflow-hidden rounded-2xl cursor-pointer"
          >
            <div className="w-full h-72 md:h-96">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                {featured.category}
              </span>
              <span className="bg-white/90 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                {featured.type}
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-white font-black text-xl md:text-2xl leading-tight mb-3 group-hover:text-orange-300 transition-colors">
                {featured.title}
              </h3>
              <p className="text-white/70 text-sm line-clamp-2 mb-4">{featured.excerpt}</p>
              <div className="flex items-center gap-4 text-white/60 text-xs">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {featured.readTime} de leitura
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {featured.views.toLocaleString("pt-BR")} visualizações
                </span>
                <span>{formatCanonicalDateForDisplay(featured.date)}</span>
              </div>
            </div>
          </Link>

          <div className="lg:col-span-2 flex flex-col gap-4">
            {others.map((article) => (
              <Link
                key={article.id}
                to={`/artigo/${article.slug}`}
                className="group flex gap-4 bg-white rounded-2xl p-4 border border-gray-100 hover:border-orange-200 transition-all cursor-pointer"
              >
                <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-orange-500 text-xs font-bold">{article.category}</span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {article.type}
                    </span>
                  </div>
                  <h3 className="text-gray-800 font-bold text-sm leading-snug mt-1 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 text-gray-400 text-xs">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views.toLocaleString("pt-BR")}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
